import moment, { Moment } from 'moment';
import { v4 as uuid } from 'uuid';
import { DateFormat } from 'utils/constants';
import { MarketPeriodInterface } from 'features/dispatcher/dispatcherTypes';
import { convertToNumber } from 'utils';

export enum ProductionModelCharacteristics {
  production_model = 'production_model'
}

export enum ProductionModelProfileType {
  COMMON = 'COMMON',
  WORKDAYS_WEEKENDS = 'WORKDAYS_WEEKENDS'
}

interface ProductionModelDateRange {
  startDate: string;
  endDate: string;
}

interface ProductionModelPeriod {
  periodStart: string;
  commonProductionMwh: number | string | null;
  workdaysProductionMwh: number | string | null;
  weekendProductionMwh: number | string | null;
}

export interface ProductionModel {
  dateRange: ProductionModelDateRange;
  id: string;
  isDefault: boolean;
  marketPeriods: ProductionModelPeriod[];
  profileType: ProductionModelProfileType;
}

export interface ProductionModels {
  productionModels: ProductionModel[];
}

export type ProductionType =
  | 'commonProductionMwh'
  | 'workdaysProductionMwh'
  | 'weekendProductionMwh';

export interface ValidationError {
  id: string;
  productionType: ProductionType;
  periodStart: string;
  message: string;
}

export interface ProductionModelChangeHandler {
  (
    id: string,
    period: string,
    value: string,
    productionType: ProductionType
  ): void;
}

const beforeGrandmasGrandma = '1900-01-01';

export const createEmptyProductionModel = (
  profileType: ProductionModelProfileType,
  marketPeriods: MarketPeriodInterface[],
  productionModels: ProductionModel[]
): ProductionModel => {
  const firstPossibleDate = productionModels.reduce(
    (acc: Moment | undefined, model) => {
      const modelEndDate = moment(
        model.dateRange.endDate,
        DateFormat.REST_DATE_FORMAT
      );
      return modelEndDate.isAfter(acc ?? moment(beforeGrandmasGrandma))
        ? modelEndDate
        : acc;
    },
    undefined
  );

  return {
    dateRange: {
      startDate: moment(firstPossibleDate)
        .add(firstPossibleDate ? 1 : 0, 'month')
        .startOf('month')
        .format(DateFormat.REST_DATE_FORMAT),
      endDate: moment(firstPossibleDate)
        .add(firstPossibleDate ? 1 : 0, 'month')
        .endOf('month')
        .format(DateFormat.REST_DATE_FORMAT)
    },
    id: uuid(),
    isDefault: false,
    marketPeriods: marketPeriods.map(
      (period): ProductionModelPeriod => ({
        periodStart: period.timeFrom,
        commonProductionMwh: null,
        workdaysProductionMwh: null,
        weekendProductionMwh: null
      })
    ),
    profileType
  };
};

export const prepareProductionModelsForApi = (
  productionModels: ProductionModel[]
): ProductionModel[] =>
  productionModels.map((model) => ({
    ...model,
    marketPeriods: model.marketPeriods.map((period) => ({
      ...period,
      commonProductionMwh: convertToNumber(period.commonProductionMwh) ?? null,
      workdaysProductionMwh:
        convertToNumber(period.workdaysProductionMwh) ?? null,
      weekendProductionMwh: convertToNumber(period.weekendProductionMwh) ?? null
    }))
  }));

export const doTheDateRangesOverlap = (
  dateRange1: ProductionModelDateRange,
  dateRange2: ProductionModelDateRange
): boolean => {
  return (
    !(
      moment(dateRange1.startDate).isBefore(moment(dateRange2.startDate)) &&
      moment(dateRange1.endDate).isBefore(moment(dateRange2.startDate))
    ) &&
    !(
      moment(dateRange1.startDate).isAfter(moment(dateRange2.endDate)) &&
      moment(dateRange1.endDate).isAfter(moment(dateRange2.endDate))
    )
  );
};

export const getProductionModelErrorMessage = (
  inputError: boolean,
  overlappingError: boolean,
  rangeError: boolean
) => {
  const messageRoot = 'module.crossModule.productionModel.listOfModels.errors';

  if (overlappingError) return messageRoot + '.overlappingDateRange';
  if (rangeError) return messageRoot + '.rangeError';
  if (inputError) return messageRoot + '.validationErrorInModel';

  return undefined;
};

const valueToString = (value: string | number | null): string =>
  value?.toString() ?? '';

export const validateProductionModels = (
  productionModels: ProductionModel[],
  validationFn: (value: string) => string | undefined
): ValidationError[] => {
  const validationErrors: ValidationError[] = [];

  productionModels.forEach(({ marketPeriods, profileType, id }) => {
    marketPeriods.forEach((period) => {
      if (profileType === ProductionModelProfileType.COMMON) {
        const message = validationFn(valueToString(period.commonProductionMwh));
        if (message) {
          validationErrors.push({
            id,
            message,
            periodStart: period.periodStart,
            productionType: 'commonProductionMwh'
          });
        }
      }

      if (profileType === ProductionModelProfileType.WORKDAYS_WEEKENDS) {
        const workdaysMessage = validationFn(
          valueToString(period.workdaysProductionMwh)
        );
        if (workdaysMessage) {
          validationErrors.push({
            id,
            message: workdaysMessage,
            periodStart: period.periodStart,
            productionType: 'workdaysProductionMwh'
          });
        }

        const weekendMessage = validationFn(
          valueToString(period.weekendProductionMwh)
        );
        if (weekendMessage) {
          validationErrors.push({
            id,
            message: weekendMessage,
            periodStart: period.periodStart,
            productionType: 'weekendProductionMwh'
          });
        }
      }
    });
  });

  return validationErrors;
};

export const nullProductionInMarketPeriods = (
  marketPeriods: ProductionModelPeriod[]
) => {
  return marketPeriods.map((period) => ({
    ...period,
    commonProductionMwh: null,
    workdaysProductionMwh: null,
    weekendProductionMwh: null
  }));
};
